import React, {useEffect, useState, useRef, useCallback} from 'react';
import {useRouter} from "next/router";
import {CmpProvider, CmpPurpose, LocalStorageType, PageType} from "../helper/enums"
import { AudioPlayer, NavigationEntry, PersonalNavigationEntry, Subscription, TeaserEntry, AppInfo, AndroidPurchase, Platform } from '../helper/interfaces';

import { check, closePianoOffer, sync, term } from '../helper/piano'
import { getPianoAid, getPianoIapPlusHalfYearProductId, getPianoIapPlusHalfYearProductIdAndroid, getPianoIapPlusMonthlyProductId, getPianoIapPlusMonthlyProductIdAndroid, getPianoIapPlusYearlyProductId, 
  getPianoIapPlusYearlyProductIdAndroid, getPianoIapPurProductId, getPianoIapPurProductIdAndroid, getPianoPlusResourceId, getPianoPurResourceId, getTermIdFromIApId, getTermIdFromAndroidProductId } from '../helper/url';
import { useQueryInAppPurchases } from '../helper/useQueryInAppPurchases';
import { logBugsnagError } from '../lib/bugsnag';
import { sendMessageToApp } from '../lib/useUser';
import { update } from '../helper/sso';
import { sendTrackingEvent } from '../components/Tracking';
import { TrackingInformationInterface } from '../helper/PageInformationInterface';
import { trackSubscriptionWithSnowPlow } from '../helper/snowplow';

declare global {
  interface Window {
    __cmp: any;
    __tcfapi: any
    googletag: any;
    validInAppPurchases: (data: [{termId: string, validUntilUTCms: string, newPurchase?: boolean, trackingInformation?: TrackingInformationInterface}]) => void;
    validInAppPurchasesAndroid: (data: AndroidPurchase[]) => void;
  }
}

function parseJSON(value: string | null): any | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '')
  } catch (error) {
    logBugsnagError(error as Error, (event) => {
      event.addMetadata('parseJSON', {
        json: value
      })
    })
    return undefined
  }
}

function stringToJson(value: string) {
  if(value) {
    try {
      if(typeof value === 'string') {
        // First handle array strings with escaped quotes
        let jsonString = value.replace(/"\[([^\]]*)\]"/g, (match, content) => {
            // Convert escaped quotes back to regular quotes inside the array
            let fixedContent = content.replace(/\\"/g, '"');
            return `[${fixedContent}]`;
        });

        // Fix escaped backslashes followed by forward slashes (e.g. \\/)
        jsonString = jsonString.replace(/\\\\\//g, "/");
        
        // Fix regular escaped forward slashes
        jsonString = jsonString.replace(/\\\//g, "/");

        // Handle any remaining escaped backslashes
        jsonString = jsonString.replace(/\\\\/g, "\\");

        // convert any literal CR or LF into "\n"
        jsonString = jsonString.replace(/(\r\n|\r|\n)/g, "\\n");
        
        return parseJSON(jsonString)
      }
      return parseJSON(value)
    }
    catch(error) {
      logBugsnagError(error as Error, (event) => {
        event.addMetadata('stringToJson', {
          json: value
        })
      })
    }
  }

  return ''
}

function normalizeTrackingInformation(value?: TrackingInformationInterface | string | null): TrackingInformationInterface {
  if(!value) {
    return {}
  }

  if(typeof value === 'string') {
    const parsedValue = stringToJson(value)
    if(parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
      return parsedValue as TrackingInformationInterface
    }

    return {}
  }

  return value
}

let lastSyncedSubscription: Subscription | null = null
const sessionId = Math.random()

async function syncSubscriptionsToPiano(updatedSubscription: Subscription, tokenId: string, user: any) {
  let json = {}
  try {
    // logBugsnagError("pre syncSubscriptionsToPiano", (event) => {
    //     event.addMetadata('lastSyncedSubscription', { lastSyncedSubscription: lastSyncedSubscription })
    //     event.addMetadata('updatedSubscription', { updatedSubscription: updatedSubscription })
    //     event.addMetadata('skipped', { skipped: false, sessionId: sessionId })
    //     event.addMetadata('json', { json: json })
    //     event.addMetadata('user', { user: user })
    //   })
    if(!lastSyncedSubscription || lastSyncedSubscription?.nnPlusWeb != updatedSubscription.nnPlusWeb || lastSyncedSubscription?.purWeb != updatedSubscription.purWeb) {
      const oldLastSyncedSubscription = lastSyncedSubscription
      lastSyncedSubscription = updatedSubscription
      json = await sync(updatedSubscription, tokenId)
      // logBugsnagError("syncSubscriptionsToPiano", (event) => {
      //   event.addMetadata('lastSyncedSubscription', { lastSyncedSubscription: oldLastSyncedSubscription })
      //   event.addMetadata('updatedSubscription', { updatedSubscription: updatedSubscription })
      //   event.addMetadata('skipped', { skipped: false, sessionId: sessionId })
      //   event.addMetadata('json', { json: json })
      //   event.addMetadata('user', { user: user })
      // })
    }
    // else {
    //   logBugsnagError("syncSubscriptionsToPiano", (event) => {
    //     event.addMetadata('lastSyncedSubscription', { lastSyncedSubscription: lastSyncedSubscription })
    //     event.addMetadata('updatedSubscription', { updatedSubscription: updatedSubscription })
    //     event.addMetadata('skipped', { skipped: true, sessionId: sessionId })
    //     event.addMetadata('json', { json: json })
    //     event.addMetadata('user', { user: user })
    //   })
    // }
  }
  catch (error) {
    logBugsnagError(new Error(error.toString()))
  }
  return json
}


type AppContext = { 
  isAllowedByCmp: (provider: string) => boolean;
  headerDisabled: boolean ;
  setPageData: (pageType: PageType, pageId?: string) => void;
  pageType: PageType;
  pageId?: string | null;
  audioPlayer: AudioPlayer | null;
  setAudioPlayer: (player: AudioPlayer | null) => void;
  user?: any;
  setUser: (value: any | null) => void;
  subscription: Subscription;
  validateSubscription: (subs?: Subscription, tokenId?: string) => Promise<Subscription>;
  setSubscription: (value: Subscription) => void
  pianoInitialized: boolean;
  setPianoInitialized: (value: boolean) => void
  adsReady: boolean;
  setCmpReady: (value: boolean) => void;
  cmpReady: boolean;
  personalResorts: PersonalNavigationEntry[] ;
  setPersonalResorts: (value: PersonalNavigationEntry[]) => void;
  appVersion: string;
  setAppVersion: (value: string) => void;
  pushSubscriptionId: string;
  setPushSubscriptionId: (value: string) => void;
  affiliateShow: boolean;
  setAffiliateShow: (value: boolean) => void;
  bookmarkedTeaserIds: string[] ;
  setBookmarkedTeaserIds: (value: string[]) => void;
  debugLogs: string;
  debugLog: (value: string) => void;
  appInfo: AppInfo;
  setAppInfo: (value: AppInfo) => void;
}

export const AppContext = React.createContext<AppContext>(
  {} as AppContext
);

type AppContextProviderProps = {
  children: React.ReactNode;
};

enum UserQueryStatusEnum {
  None,
  Query,
  Receive
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
  const [queryInAppPurchases] = useQueryInAppPurchases()

  const [vendorConsents, setVendorConsents] = useState(undefined);
  const [purposeConsents, setPurposeConsents] = useState({});
  const [pageType, setPageType] = useState<PageType>(PageType.Homepage);
  const [pageId, setPageId] = useState<string | null>();
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayer | null>(null);
  const [user, setUserInternal] = useState<any | null>();
  const [subscription, setSubscriptionInternal] = useState<Subscription>({
    purWeb: false,
    nnPlusWeb: false,
    purApp: false,
    nnPlusApp: false,
    validated: false,
    nnPlusValidUntil: null,
    purValidUntil: null,
    nnPlusTermId: "",
    nnPlusReceiptData: "",
    purTermId: "",
    purReceiptData: "",
  });
  const [pianoInitialized, setPianoInitialized] = useState(false);
  const [adsReady, setAdsReady] = useState(false);
  const [cmpReady, setCmpReady] = useState(false);
  const [appVersion, setAppVersion] = useState<string>("unknown");
  const [pushSubscriptionId, setPushSubscriptionId] = useState<string>("");  
  const [affiliateShow,setAffiliateShow] = useState(false);
  const [appInfo, setAppInfo] = useState<AppInfo>({
    name: '',
    version: '',
    platform: Platform.Unknown,
    pushesEnabled: false,
    pushSubscriptionId: '',
    iapAvailable: false
  });

  const [personalResorts, setPersonalResortsInternal] = useState<PersonalNavigationEntry[]>([]);
  const [bookmarkedTeaserIds, setBookmarkedTeaserIdsInternal] = useState<string[]>([]);

  const [internalDebugLogs, setInternalDebugLogs] = useState<string>("");

  const [userQueryStatus, setUserQueryStatus] = useState<UserQueryStatusEnum>(UserQueryStatusEnum.None);

  const syncRef = useRef(false);  

  const setSubscription = useCallback((value: Subscription) => {
    setSubscriptionInternal(value)
  }, [setSubscriptionInternal])
  
  
  const setPageData = (pageType: PageType, pageId?: string) => {
    setPageType(pageType)
    setPageId(pageId)

    if(window && window.setLocalStorageItemWithNotify) {
      window.setLocalStorageItemWithNotify(LocalStorageType.PageId, pageId ?? '')
    }
  }

  const isAllowedByCmp = useCallback((provider: string) => {
    if(!cmpReady|| vendorConsents == undefined) {
      return false
    }
    
    return (vendorConsents![provider] === true)
  }, [vendorConsents, cmpReady]);

  const hasPurposeByCmp = (provider: string) => {
    if(purposeConsents != undefined) {
      return (purposeConsents![provider] === true)
    }
    return false
  };
  
  const updateCmpInfos = useCallback((e,o) =>
  {
    var cmpData = window.__cmp('getCMPData');
    if(typeof cmpData != "undefined") {
        if ("vendorConsents" in cmpData) {
          setVendorConsents(cmpData.vendorConsents)
        }
    }

    //var tcData = window.__tcfapi('getTCData');
    if(typeof cmpData != "undefined") {
      setPurposeConsents(cmpData.purposeConsents)
    }
  }, [cmpReady])

  useEffect(() => {
    if(cmpReady) {
      updateCmpInfos('', '')
    }
  }, [cmpReady])

  useEffect(() => {
    queryInAppPurchases()
  }, [])  

  useEffect(() => { 
    if(window && window.__cmp) {
      window.__cmp("addEventListener",["consent",updateCmpInfos,false],null);
      return () => window.__cmp("removeEventListener",["consent",updateCmpInfos,false],null)
    }
  }, [updateCmpInfos])

  useEffect(() => { 
    if(window && window.__cmp) {
      window.__cmp("addEventListener",["consentcustom",updateCmpInfos,false],null);
      return () => window.__cmp("removeEventListener",["consentcustom",updateCmpInfos,false],null)
    }
  }, [updateCmpInfos])

  const router = useRouter();
  const [headerDisabledInternal] = useState((process.env.NEXT_PUBLIC_FORCE_APP_MODE  === "true" || (router.query && router.query.iosapp && router.query.iosapp === "1" ) || router.asPath.includes("iosapp=1") || router.asPath.includes("/navigation") || router.asPath.includes("/user")) ? true : false);

  const setUserInternalWrapper = (value: any | null) => {
    setUserInternal(value);
    sendMessageToApp(value, debugLog);
  }
  useEffect(() => {
    window.localStorageItemCallback = (key: string, value: string) => {
      // console.log("localStorageItemCallback key: " + key)
      const processValue = (value != "null") ? value : null
      if(key == LocalStorageType.User && processValue) {
        setUserInternalWrapper(stringToJson(processValue))
        debugLog("* localStorageItemCallback user")
        if(userQueryStatus === UserQueryStatusEnum.Query) {
          setUserQueryStatus(UserQueryStatusEnum.Receive)
        }
      }
      if(key == LocalStorageType.PageId) {
        setPageId(processValue)
      }
      if(key == LocalStorageType.PersonalResorts && processValue) {
        setPersonalResortsInternal(stringToJson(processValue) ?? [])
      }
      if(key == LocalStorageType.BookmarkIds && processValue) {
        setBookmarkedTeaserIdsInternal(stringToJson(processValue) ?? [])
      }
    }

    if(headerDisabledInternal) {
      if(window && window.queryLocalStorageItem) {
        window.queryLocalStorageItem(LocalStorageType.User)
        setUserQueryStatus(UserQueryStatusEnum.Query)
        debugLog("* queryLocalStorageItem user")
        window.queryLocalStorageItem(LocalStorageType.PersonalResorts)
        window.queryLocalStorageItem(LocalStorageType.BookmarkIds)
      }
    }
    else {
      setUserInternalWrapper(stringToJson(window.localStorage.getItem(LocalStorageType.User) ?? ''))
      setPersonalResortsInternal(stringToJson(window.localStorage.getItem(LocalStorageType.PersonalResorts) ?? '') ?? [])
      setBookmarkedTeaserIdsInternal(stringToJson(window.localStorage.getItem(LocalStorageType.BookmarkIds) ?? '') ?? [])
    }
  }, [headerDisabledInternal])

  const setUser = useCallback((value: any | null) => {
    debugLog("* newValue set user 1 " +  JSON.stringify(value))
    setUserInternalWrapper(value)
    const newValue = JSON.stringify(value)

    debugLog("* newValue set user 2 " + JSON.stringify(newValue))
    if(headerDisabledInternal) {
      if(window && window.setLocalStorageItemWithNotify) {
        window.setLocalStorageItemWithNotify(LocalStorageType.User, newValue)
      }
    }
    else {
      window.localStorage.setItem(LocalStorageType.User, newValue)
    }
    
  }, [headerDisabledInternal])

  const setPersonalResorts = useCallback((value: PersonalNavigationEntry[]) => {
    setPersonalResortsInternal(value)
    const newValue = JSON.stringify(value)

    if(headerDisabledInternal) {
      if(window && window.setLocalStorageItemWithNotify) {
        window.setLocalStorageItemWithNotify(LocalStorageType.PersonalResorts, newValue)
      }
    }
    else {
      window.localStorage.setItem(LocalStorageType.PersonalResorts, newValue)
    }    
    
  }, [headerDisabledInternal, setPersonalResortsInternal])

  const setBookmarkedTeaserIds = useCallback((value: string[]) => {
    setBookmarkedTeaserIdsInternal(value)
    const newValue = JSON.stringify(value)

    if(headerDisabledInternal) {
      if(window && window.setLocalStorageItemWithNotify) {
        window.setLocalStorageItemWithNotify(LocalStorageType.BookmarkIds, newValue)
      }
    }
    else {
      window.localStorage.setItem(LocalStorageType.BookmarkIds, newValue)
    }    
    
  }, [headerDisabledInternal, setBookmarkedTeaserIdsInternal])

  function isPaidProductId(productId: string): boolean {
    return productId == getPianoIapPlusMonthlyProductId() || productId == getPianoIapPlusHalfYearProductId() || productId == getPianoIapPlusYearlyProductId()
  }

  function isPurProductId(productId: string): boolean {
    return productId == getPianoIapPurProductId()
  }
  function isPaidProductIdAndroid(productId: string): boolean {
    return productId == getPianoIapPlusMonthlyProductIdAndroid() || productId == getPianoIapPlusHalfYearProductIdAndroid() || productId == getPianoIapPlusYearlyProductIdAndroid()
  }
  function isPurProductIdAndroid(productId: string): boolean {
    return productId == getPianoIapPurProductIdAndroid()
  }

  function getProductName(productId): string {
    if(productId == getPianoIapPlusMonthlyProductId()) {
      return "nnPlusMonthly"
    }
    if(productId == getPianoIapPlusHalfYearProductId()) {
      return "nnPlusHalfYear"
    }
    if(productId == getPianoIapPlusYearlyProductId()) {
      return "nnPlusYearly"
    }
    if(productId == getPianoIapPurProductId()) {
      return "pur"
    }
    return "unknown"
  }
  useEffect(() => {
    window.validInAppPurchases = async (data: any) => {
      let pur = false
      let purValidUntil = null
      let purTermId: string | null = null
      let purReceiptData = null
      let plus = false
      let plusValidUntil = null
      let plusTermId: string | null = null
      let plusReceiptData = null
      let isNewPurchase = false

      if(data && data.length > 0) {
        for(let i = 0; i < data.length; i++) {
          const element = data[i]
          debugLog("validInAppPurchases " + JSON.stringify(element))
          debugLog("isPaidProductId: " + isPaidProductId(element.termId))
          debugLog("isPurProductId: " + isPurProductId(element.termId))
          if(element.newPurchase == true) {
            const termId = getTermIdFromIApId(element.termId)
            const trackingInformation = normalizeTrackingInformation(element.trackingInformation)
            const pianoTerm = await term(termId)
            let goalType = element.termId

            if(pianoTerm?.term?.name) {
              goalType = pianoTerm?.term?.name
              trackSubscriptionWithSnowPlow(pianoTerm?.term?.name, '0', pianoTerm?.term?.type);
            }
            else {
              trackSubscriptionWithSnowPlow(element.termId, '0', 'iOS iAP');
            }

            trackingInformation.goalType = goalType
            sendTrackingEvent('checkoutComplete', trackingInformation, true, subscription, user, appInfo);
            isNewPurchase = true
          }

          if(isPaidProductId(element.termId)) {
            plus = true
            plusValidUntil = element.validUntilUTCms
            plusTermId = getTermIdFromIApId(element.termId) ?? ''
            plusReceiptData = element.receiptData
          }
          if(isPurProductId(element.termId)) {
            pur = true
            purValidUntil = element.validUntilUTCms
            purTermId = getTermIdFromIApId(element.termId)
            purReceiptData = element.receiptData
          }
        }
      }

      let subs: Subscription = {
        ...subscription,
        validated: true,
        nnPlusApp: plus,
        nnPlusValidUntil: plusValidUntil,
        nnPlusTermId: plusTermId ?? '',
        nnPlusReceiptData: plusReceiptData ?? '',
        purApp: pur,
        purValidUntil: purValidUntil,
        purTermId: purTermId ?? '',
        purReceiptData: purReceiptData ?? ''
      }
      if(isNewPurchase) {
        if(user?.tokenId) {
          syncSubscriptionsToPiano(subs, user.tokenId, user)
        }
        closePianoOffer()
      }
      validateSubscription(subs)
    }

    window.validInAppPurchasesAndroid = async (data: AndroidPurchase[]) => {
      let pur = false
      let purValidUntil: number | null = null
      let purTermId: string | null = null
      let purReceiptData: string | null = null
      let plus = false
      let plusValidUntil: number | null = null
      let plusTermId: string | null = null
      let plusReceiptData: string | null = null

      let isNewPurchase = false
      if(data && data.length > 0) {
        for(let i = 0; i < data.length; i++) {
          const element = data[i]
          const termId = getTermIdFromAndroidProductId(element.productId)
          debugLog("validInAppPurchasesAndroid " + JSON.stringify(element))
          debugLog("isPaidProductId: " + isPaidProductIdAndroid(element.productId))
          debugLog("isPurProductId: " + isPurProductIdAndroid(element.productId))
          debugLog("termId: " + termId)
          if(element.newPurchase == true && termId) {
            const trackingInformation = normalizeTrackingInformation(element.trackingInformation)
            const pianoTerm = await term(termId)
            let goalType = termId

            if(pianoTerm?.term?.name) {
              goalType = pianoTerm?.term?.name
              trackSubscriptionWithSnowPlow(pianoTerm?.term?.name, '0', pianoTerm?.term?.type);
            }
            else {
              trackSubscriptionWithSnowPlow(termId, '0', 'AndroidiAP');
            }
            trackingInformation.goalType = goalType
            sendTrackingEvent('checkoutComplete', trackingInformation, true, subscription, user, appInfo);
            isNewPurchase = true
          }

          // now + 1 day
          const validUntilUTCms = new Date().getTime() + 24 * 60 * 60 * 1000

          if(isPaidProductIdAndroid(element.productId)) {
            plus = true
            plusValidUntil = validUntilUTCms
            plusTermId = termId ?? ''
            plusReceiptData = element.purchaseToken ?? ''
          }
          if(isPurProductIdAndroid(element.productId)) {
            pur = true
            purValidUntil = validUntilUTCms
            purTermId = termId ?? ''
            purReceiptData = element.purchaseToken ?? ''
          }
        }
      }

      let subs = {
        ...subscription,
        validated: true,
        nnPlusApp: plus,
        nnPlusValidUntil: plusValidUntil,
        nnPlusTermId: plusTermId ?? '',
        nnPlusReceiptData: plusReceiptData ?? '',
        purApp: pur,
        purValidUntil: purValidUntil,
        purTermId: purTermId ?? '',
        purReceiptData: purReceiptData ?? ''
      }
      if(isNewPurchase) {
        if(user?.tokenId) {
          syncSubscriptionsToPiano(subs, user.tokenId, user)
        }
        closePianoOffer()
      }
      validateSubscription(subs)
    }
  },[subscription, setSubscription])

  const validateSubscription = async(subs: Subscription, tokenId?: string) => {
    debugLog("validateSubscription" + JSON.stringify(subs))
    let userTokenId = tokenId ?? user?.tokenId
    let currentSubs = subs ?? subscription
    if(userTokenId) {
      let purResponse = await check(getPianoPurResourceId(), getPianoAid(), userTokenId, debugLog)
      let plusResponse = await check(getPianoPlusResourceId(), getPianoAid(), userTokenId, debugLog)

      debugLog("purResponse" + JSON.stringify(purResponse))
      debugLog("plusResponse" + JSON.stringify(plusResponse))

      const updatedSubscription = {
        ...currentSubs, 
        validated: true, 
        purWeb: (purResponse && purResponse.access && purResponse.access.granted) ? true : false, 
        nnPlusWeb: (plusResponse && plusResponse.access && plusResponse.access.granted) ? true : false, 
      }

      if(!syncRef.current && ((!updatedSubscription.purWeb && updatedSubscription.purApp) || (!updatedSubscription.nnPlusWeb && updatedSubscription.nnPlusApp))) {
        debugLog("updatedSubscription " + JSON.stringify(updatedSubscription))
        debugLog("user.tokenId " + JSON.stringify(userTokenId))
        if(user && userTokenId == user?.tokenId) {
          let json = await syncSubscriptionsToPiano(updatedSubscription, userTokenId, user)
          syncRef.current = true
          debugLog("json " + JSON.stringify(json))
        }
      }

      setSubscription(updatedSubscription)
      return updatedSubscription
    }
    else {
      const updatedSubscription = {
        ...currentSubs, 
        validated: true, 
        purWeb: false,
        nnPlusWeb:false,
      } 
      setSubscription(updatedSubscription)
      return updatedSubscription
    }
  }

  useEffect(() => {
    validateSubscription(subscription, undefined)
  }, [user?.tokenId])

  useEffect(() => {
    function trfReady(event) {
      //console.log('** traffectiveApiReady event received', event);
      setAdsReady(true);
    }
    
    // Check if traffective is already available
    if (window.traffective) {
      //console.log('** window.traffective already available, setting adsReady');
      setAdsReady(true);
    }
    
    // Listen for the custom event
    if(document) {
      document.addEventListener('traffectiveApiReady', trfReady);
      console.log('** traffectiveApiReady listener registered');
      
      // Fallback: Poll for window.traffective availability
      let pollInterval: NodeJS.Timeout | null = null;
      let adsReadySet = false;
      const checkTraffective = () => {
        if (window.traffective && !adsReadySet) {
          //console.log('** window.traffective detected via polling, setting adsReady');
          adsReadySet = true;
          setAdsReady(true);
        }
      };
      
      // Poll every 100ms for up to 10 seconds
      let attempts = 0;
      const maxAttempts = 100;
      pollInterval = setInterval(() => {
        attempts++;
        checkTraffective();
        if (attempts >= maxAttempts || window.traffective) {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, 100);
      
      return () => {
        //console.log('** traffectiveApiReady listener removed');
        document.removeEventListener('traffectiveApiReady', trfReady);
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      };
    }
  }, []);

  useEffect(() => {
    let googletag = window.googletag || {cmd: []};
    googletag.cmd.push(function () {
        googletag.pubads().addEventListener('slotResponseReceived', function (event) {
            try {
                var slot = event.slot;
                var slotId = slot.getSlotElementId();
                var prohibitedSlotId = ["ad-sticky", "ad-Interstitial_1", "ad-Mobile_Sticky", "ad-OutOfPage"];
                if (prohibitedSlotId.includes(slotId) || !slotId.startsWith('ad-')) {
                    return;
                }
                var slotElem = document.querySelector('#' + slotId);
                slotElem?.classList?.add('ad-container–declaration');
                // if (slotId === "ad-sticky") {
                //     observeStickyAd(slotElem, footer, pianoRibbon);
                // }
            } catch (error) {
                console.error("Advert could not be styled", error);
            }
        });
    });
  }, [])

  const updateLoggedInUser = async () => {
    debugLog("* updateLoggedInUser " + JSON.stringify(user))
    if(user && user.tokenId) {
      const data = await update(user.tokenId);
      debugLog("* data " + JSON.stringify(data))
      if (data.user) {
        setUser({...user, 
          userName: data.user.userName, 
          userSurname: data.user.userSurname, 
          userEmail: data.user.userEmail});
      }
    }
  }

  useEffect(() => {
    if(userQueryStatus === UserQueryStatusEnum.Receive) {
      updateLoggedInUser();
      setUserQueryStatus(UserQueryStatusEnum.None);
    }
  }, [userQueryStatus])

  const debugLog = (value: string) => {
    if(process.env.NEXT_PUBLIC_APP_ENV != "production") {
      setInternalDebugLogs(`${internalDebugLogs}<br>${new Date().toISOString()}<br>${value}`);
    }
  }


  return (
    <AppContext.Provider value={{ isAllowedByCmp, headerDisabled: headerDisabledInternal, setPageData, pageType, pageId, audioPlayer, 
      setAudioPlayer, user, setUser, subscription, validateSubscription, setSubscription, pianoInitialized, setPianoInitialized, 
      adsReady, setCmpReady, cmpReady, personalResorts, setPersonalResorts, appVersion, setAppVersion, pushSubscriptionId, 
      setPushSubscriptionId, affiliateShow, setAffiliateShow, bookmarkedTeaserIds, setBookmarkedTeaserIds, debugLogs: internalDebugLogs, debugLog, appInfo, setAppInfo }}>
      {children}
    </AppContext.Provider>
  );
};